import React, { useState, useEffect } from 'react';
import { Search, Send, Building2, Phone, Users, User, Image, Mic, Video, FileText, MapPin, ChevronDown, ChevronUp, Megaphone, Smile } from 'lucide-react';
import { getConversations, getSectors, getWhatsAppNumbers, getUsers, markConversationAsRead } from '../services/api';
import ConversationView from './ConversationView';
import { useLanguage } from '../contexts/LanguageContext';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';

function ConversationsList() {
  const { t } = useLanguage();
  // ⭐ NOVO: escuta o mesmo aviso em tempo real que já dispara a notificação do
  // Chrome — assim a lista atualiza na hora que chega mensagem nova, sem precisar
  // esperar os 8s do polling nem trocar de página pra "forçar" a atualização
  const { notifications } = useRealtimeNotifications();
  // ⭐ NOVO: filtros recolhíveis (lembra a preferência do usuário)
  const [filtersOpen, setFiltersOpen] = useState(() => localStorage.getItem('conversations_filters_open') !== 'false');

  // ⭐ NOVO: exibe a última mensagem com ícone + rótulo, igual o WhatsApp faz
  const renderLastMessagePreview = (conv) => {
    const type = conv.last_message_type;
    const iconClass = "inline-block mr-1 -mt-0.5";

    switch (type) {
      case 'image':
        return <><Image size={13} className={iconClass} />Foto</>;
      case 'audio':
        return <><Mic size={13} className={iconClass} />Áudio</>;
      case 'video':
        return <><Video size={13} className={iconClass} />Vídeo</>;
      case 'document':
        return <><FileText size={13} className={iconClass} />Documento</>;
      case 'location':
        return <><MapPin size={13} className={iconClass} />Localização</>;
      case 'sticker':
        return <><Smile size={13} className={iconClass} />Figurinha</>;
      case 'reaction':
        // ⭐ NOVO: mostra a reação mais recente na prévia, quando ela for a
        // atividade mais nova da conversa (antes nunca aparecia aqui)
        return <>Reagiu {conv.last_reaction_emoji || ''}</>;
      default:
        return conv.last_message || t('conversations.noMessages');
    }
  };

  // ⭐ TABS agora usa tradução
  const TABS = [
    { key: 'all', label: t('conversations.tabAll') },
    { key: 'open', label: t('conversations.tabOpen') },
    { key: 'pending', label: t('conversations.tabPending') },
    { key: 'closed', label: t('conversations.tabClosed') },
    { key: 'ctwa', label: 'CTWA' },
  ];

  // ⭐ NOVO: opções do seletor de bolinhas (status da janela de 24h)
  const WINDOW_OPTIONS = [
    { key: 'all',     label: 'Todas',     color: '#9CA3AF' },
    { key: 'active',  label: 'Ativa',     color: '#22C55E' },
    { key: 'warning', label: 'Expirando', color: '#F59E0B' },
    { key: 'expired', label: 'Expirada',  color: '#EF4444' },
  ];

  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [sectors, setSectors] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // ⭐ NOVO: estado do filtro por status da janela (seletor de bolinhas)
  const [windowFilter, setWindowFilter] = useState('all');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  // ⭐ NOVO: paginação (evita baixar e desenhar milhares de conversas de uma vez)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;

  // ⭐ NOVO: busca com atraso (só manda pro servidor 400ms depois de parar de digitar)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Carregar dados do usuário atual
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser(payload);
      } catch (err) {
        console.error('Erro ao carregar usuário:', err);
      }
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [selectedSector]);

  // ⭐ CORRIGIDO: sempre que um filtro muda, volta pra página 1 e recarrega do zero
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    loadConversations(1, false); // primeira carga: mostra "Carregando..."

    // ⭐ NOVO: atualiza só a primeira página sozinha a cada 8s, de forma silenciosa
    // (sem esconder a lista/piscar), pra novas mensagens aparecerem
    // sem precisar sair e entrar na tela de novo
    const interval = setInterval(() => {
      loadConversations(1, false, true);
    }, 8000);

    return () => clearInterval(interval);
  }, [selectedSector, selectedPhone, selectedUser, activeTab, debouncedSearch]);

  // ⭐ NOVO: sempre que chegar um aviso em tempo real (mesmo mecanismo do sino/
  // notificação do navegador), atualiza a lista na hora — sem esperar o polling
  useEffect(() => {
    if (notifications.length === 0) return;
    loadConversations(1, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  // ⭐ NOVO: carrega mais conversas quando o usuário rola até perto do fim da lista
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 150 && hasMore && !loadingMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadConversations(nextPage, true);
    }
  };

  // ⭐ NOVO: fecha o menu do seletor de bolinhas ao clicar fora dele
  useEffect(() => {
    if (!statusMenuOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-window-status-selector]')) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusMenuOpen]);

  const loadFilters = async () => {
    try {
      const [sectorsRes, phonesRes] = await Promise.all([
        getSectors(),
        getWhatsAppNumbers()
      ]);
      setSectors(sectorsRes.data);
      setPhoneNumbers(phonesRes.data);
    } catch (err) {
      console.error('Erro ao carregar filtros:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      if (selectedSector) {
        setUsers(res.data.filter(u => u.sector_id == selectedSector));
      } else {
        setUsers(res.data);
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setUsers([]);
    }
  };

  // ⭐ CORRIGIDO: agora busca por página, com todos os filtros aplicados no servidor
  // (setor, número, atendente, aba de status e busca) — antes baixava tudo de uma vez
  const loadConversations = async (pageNum = 1, append = false, isPollingRefresh = false) => {
    const isFirstLoad = pageNum === 1 && !append && !isPollingRefresh;
    if (isFirstLoad) setLoading(true);
    if (append) setLoadingMore(true);
    try {
      const filters = { page: pageNum, limit: PAGE_SIZE };
      if (selectedSector) filters.sector_id = selectedSector;
      if (selectedUser) filters.assigned_to = selectedUser;
      if (selectedPhone) filters.phone_number_id = selectedPhone;
      if (activeTab !== 'all' && activeTab !== 'ctwa') filters.status = activeTab;
      if (debouncedSearch) filters.search = debouncedSearch;

      const res = await getConversations(filters);

      if (append) {
        setConversations(prev => [...prev, ...res.data]);
      } else if (isPollingRefresh) {
        // ⭐ CORRIGIDO: antes, se o usuário já tivesse rolado além da primeira
        // página, a atualização era simplesmente ignorada (nunca aparecia nada
        // novo). Agora mescla certo: conversas atualizadas/novas sobem pro topo
        // (a própria ordenação do servidor já cuida disso), e o resto que já
        // estava carregado continua embaixo, sem duplicar e sem perder a rolagem.
        setConversations(prev => {
          const freshIds = new Set(res.data.map(c => c.id));
          const untouched = prev.filter(c => !freshIds.has(c.id));
          return [...res.data, ...untouched];
        });
      } else {
        setConversations(res.data);
      }
      setHasMore(res.data.length === PAGE_SIZE);
    } catch (err) {
      console.error('Erro ao carregar conversas:', err);
    } finally {
      if (isFirstLoad) setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  // ⭐ NOVO: calcula o status da janela de 24h com base na última mensagem RECEBIDA do cliente
  const getWindowStatus = (conv) => {
    if (!conv.last_inbound_at) return 'expired';
    const hoursLeft = 24 - (Date.now() - new Date(conv.last_inbound_at).getTime()) / 3600000;
    if (hoursLeft <= 0) return 'expired';
    if (hoursLeft <= 1) return 'warning';
    return 'active';
  };

  // ⭐ CORRIGIDO: setor, número, atendente, aba de status e busca agora são
  // filtrados no servidor (junto com a paginação) — só o filtro de janela de 24h
  // continua aqui, porque ele depende de um cálculo de hora feito no navegador
  const filtered = conversations.filter(c => {
    if (windowFilter !== 'all' && getWindowStatus(c) !== windowFilter) return false;
    // ⭐ NOVO: aba CTWA — só mostra conversas que vieram de um anúncio patrocinado
    if (activeTab === 'ctwa' && !c.ctwa_clid) return false;
    return true;
  });

  const stats = {
    total: conversations.length,
    open: conversations.filter(c => c.status === 'open').length,
    pending: conversations.filter(c => c.status === 'pending').length,
    closed: conversations.filter(c => c.status === 'closed').length,
  };

  const canFilterByUser = currentUser?.role === 'tenant_admin' || currentUser?.role === 'manager';

  // ⭐ NOVO: opção selecionada no seletor de bolinhas
  const activeWindowOption = WINDOW_OPTIONS.find(o => o.key === windowFilter);

  return (
    <div className="flex h-full bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
      {/* Painel esquerdo - Lista */}
      <div className="w-full md:w-80 2xl:w-96 flex flex-col border-r border-gray-100 dark:border-slate-700 flex-shrink-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">{t('Chats')}</h1>
            <button
              type="button"
              onClick={() => setFiltersOpen(o => {
                const next = !o;
                localStorage.setItem('conversations_filters_open', String(next));
                return next;
              })}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 flex-shrink-0"
              title={filtersOpen ? 'Recolher filtros' : 'Expandir filtros'}
            >
              {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-400 dark:text-slate-400">{t('Manage your WhatsApp chats')}</p>
            {!filtersOpen && (
              <div className="relative" data-window-status-selector>
                <button
                  type="button"
                  onClick={() => setStatusMenuOpen(o => !o)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-xs flex-shrink-0"
                  aria-haspopup="true"
                  aria-expanded={statusMenuOpen}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: activeWindowOption.color }}
                  />
                  <span className="text-gray-700 dark:text-slate-200 whitespace-nowrap">
                    {activeWindowOption.label}
                  </span>
                </button>

                {statusMenuOpen && (
                  <div className="absolute right-0 top-9 z-20 w-40 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg py-1">
                    {WINDOW_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => { setWindowFilter(opt.key); setStatusMenuOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-600 ${
                          windowFilter === opt.key ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                        }`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                        <span className="text-gray-800 dark:text-slate-100">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {filtersOpen && (
          <>
          {/* Busca */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={16} />
            <input
              type="text"
              placeholder={t('conversations.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>

          {/* Filtros de Setor, Número e Atendente */}
          <div className="space-y-2 mb-3">
            {/* Filtro por Setor */}
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={14} />
              <select
                value={selectedSector}
                onChange={e => {
                  setSelectedSector(e.target.value);
                  setSelectedUser('');
                  setSelectedPhone(''); // ⭐ CORRIGIDO: evita ficar com um número de outro setor selecionado escondido
                }}
                className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 appearance-none cursor-pointer"
              >
                <option value="" className="text-gray-900 dark:text-slate-100">🏢 {t('conversations.allSectors')}</option>
                {sectors.map(s => (
                  <option key={s.id} value={s.id} className="text-gray-900 dark:text-slate-100">📍 {s.name}</option>
                ))}
              </select>
            </div>

            {/* Filtro por Atendente (apenas para Admin/Gerente) */}
            {canFilterByUser && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={14} />
                <select
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  disabled={!selectedSector}
                  className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="" className="text-gray-900 dark:text-slate-100">
                    👤 {selectedSector ? t('conversations.allInSector') : t('conversations.selectSectorFirst')}
                  </option>
                  {users.map(u => (
                    <option key={u.id} value={u.id} className="text-gray-900 dark:text-slate-100">
                      👤 {u.name} {u.sector_name ? `(${u.sector_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filtro por Número + Seletor de status da janela (bolinhas) */}
            <div className="relative flex items-center gap-2" data-window-status-selector>
              <div className="relative flex-1 min-w-0">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" size={14} />
                <select
                  value={selectedPhone}
                  onChange={e => setSelectedPhone(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none text-gray-900 dark:text-slate-100 appearance-none cursor-pointer"
                >
                  <option value="" className="text-gray-900 dark:text-slate-100">📱 {t('conversations.allNumbers')}</option>
                  {/* ⭐ CORRIGIDO: filtra pelo setor escolhido acima — antes mostrava
                      os números de todos os setores juntos, mesmo com um selecionado */}
                  {phoneNumbers
                    .filter(p => !selectedSector || String(p.sector_id) === String(selectedSector))
                    .map(p => (
                    <option key={p.id} value={p.phone_number_id} className="text-gray-900 dark:text-slate-100">
                      {p.display_phone_number || p.phone_number || p.display_name || p.phone_number_id} ({p.sector_name || t('conversations.noSector')})
                    </option>
                  ))}
                </select>
              </div>

              {/* ⭐ NOVO: botão compacto com bolinha colorida, abre o menu de status */}
              <button
                type="button"
                onClick={() => setStatusMenuOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm flex-shrink-0"
                aria-haspopup="true"
                aria-expanded={statusMenuOpen}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: activeWindowOption.color }}
                />
                <span className="hidden sm:inline text-gray-700 dark:text-slate-200 whitespace-nowrap">
                  {activeWindowOption.label}
                </span>
              </button>

              {/* ⭐ NOVO: menu do seletor de bolinhas — responsivo (largura total no mobile) */}
              {statusMenuOpen && (
                <div className="absolute right-0 top-11 z-20 w-full sm:w-40 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg py-1">
                  {WINDOW_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => { setWindowFilter(opt.key); setStatusMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-600 ${
                        windowFilter === opt.key ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                      <span className="text-gray-800 dark:text-slate-100">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
          )}

          {/* Tabs de Status — em telas estreitas vira um dropdown, pra caber melhor */}
          <select
            value={activeTab}
            onChange={e => setActiveTab(e.target.value)}
            className="2xl:hidden w-full p-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 rounded-lg text-xs font-medium text-gray-700 dark:text-slate-200 outline-none"
          >
            {TABS.map(tab => (
              <option key={tab.key} value={tab.key}>{tab.label}</option>
            ))}
          </select>
          <div className="hidden 2xl:flex gap-1">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de Conversas */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">{t('common.loading')}</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 dark:text-slate-500 text-sm">{t('conversations.noResults')}</p>
              {(selectedSector || selectedPhone || selectedUser || windowFilter !== 'all') && (
                <button
                  onClick={() => { setSelectedSector(''); setSelectedPhone(''); setSelectedUser(''); setWindowFilter('all'); }}
                  className="mt-2 text-xs text-green-600 dark:text-green-400 hover:text-green-700"
                >
                  {t('conversations.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            filtered.map(conv => {
              return (
                                <button
                  key={conv.id}
                  onClick={() => {
                    setSelectedId(conv.id);
                    // ⭐ NOVO: zera o contador na tela na hora (sem esperar o servidor)
                    // e avisa o backend que essa conversa foi lida
                    if (conv.unread_count > 0) {
                      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
                      markConversationAsRead(conv.id).catch(err => console.error('Erro ao marcar como lida:', err));
                    }
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-50 dark:border-slate-700 text-left ${
                    selectedId === conv.id
                      ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500'
                      // ⭐ NOVO: tarja amarela — só admin/gerente vê (o campo nem
                      // existe pro atendente, já que ele usa outra consulta) —
                      // mensagem do cliente sem resposta há mais tempo do que o
                      // configurado no setor; some sozinha assim que alguém responde
                      : conv.is_stale_unanswered ? 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-l-amber-400' : ''
                  }`}
                  title={conv.is_stale_unanswered ? 'Cliente sem resposta há mais tempo que o configurado no setor' : undefined}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center justify-center font-bold text-sm">
                      {(conv.profile_name || 'U').charAt(0).toUpperCase()}
                    </div>
                    {/* ⭐ NOVO: selo da janela de 24h */}
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800 ${
                        getWindowStatus(conv) === 'active' ? 'bg-green-500' :
                        getWindowStatus(conv) === 'warning' ? 'bg-amber-500' :
                        'bg-red-500'
                      }`}
                      title={
                        getWindowStatus(conv) === 'active' ? 'Janela ativa (mais de 1h restante)' :
                        getWindowStatus(conv) === 'warning' ? 'Janela expirando (menos de 1h)' :
                        'Janela expirada — só template'
                      }
                    />
                  </div>
                                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-gray-900 dark:text-slate-100' : 'font-semibold text-gray-900 dark:text-slate-100'}`}>
                        {conv.profile_name || conv.contact_wa_id}
                      </h3>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                        <span className="text-xs text-gray-400 dark:text-slate-500">
                          {conv.last_message_at
                            ? new Date(conv.last_message_at).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })
                            : '-'}
                        </span>
                        {/* ⭐ NOVO: balãozinho verde de mensagens não lidas, igual WhatsApp */}
                        {conv.unread_count > 0 && (
                          <span className="bg-green-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                            {conv.unread_count > 99 ? '99+' : conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={`text-xs truncate mt-0.5 flex items-center ${conv.unread_count > 0 ? 'text-gray-700 dark:text-slate-200 font-medium' : 'text-gray-500 dark:text-slate-400'}`}>
                      {renderLastMessagePreview(conv)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {conv.sector_name && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[10px] rounded">
                          <Building2 size={10} />
                          {conv.sector_name}
                        </span>
                      )}
                      {/* ⭐ NOVO: etiqueta CTWA — mostra quando o contato veio de um anúncio "Clique para WhatsApp" */}
                      {conv.ctwa_clid && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 text-[10px] rounded font-bold"
                          title="Veio de um anúncio patrocinado (Click to WhatsApp)"
                        >
                          <Megaphone size={10} />
                          CTWA
                        </span>
                      )}
                      {conv.assigned_to_name && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-[10px] rounded">
                          <User size={10} />
                          {conv.assigned_to_name}
                        </span>
                      )}
                      {/* ⭐ NOVO: etiqueta de status, mesmo estilo/cores do seletor dentro do chat */}
                      {conv.status && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-[10px] rounded">
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: conv.status === 'open' ? '#2563EB' : conv.status === 'pending' ? '#F59E0B' : '#16A34A'
                            }}
                          />
                          {conv.status === 'open' ? 'Aberta' : conv.status === 'pending' ? 'Pendente' : 'Resolvido'}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
          {/* ⭐ NOVO: indicador de carregamento ao rolar pra buscar mais conversas */}
          {loadingMore && (
            <p className="text-center py-3 text-gray-400 dark:text-slate-500 text-xs">Carregando mais...</p>
          )}
        </div>

        {/* Rodapé com estatísticas */}
        <div className="p-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 text-xs text-gray-500 dark:text-slate-400 flex justify-around">
          <span>{t('conversations.total')}: <strong>{stats.total}</strong></span>
          <span>{t('conversations.open')}: <strong className="text-green-600 dark:text-green-400">{stats.open}</strong></span>
          <span>{t('conversations.pending')}: <strong className="text-yellow-600 dark:text-yellow-400">{stats.pending}</strong></span>
          <span>{t('conversations.closed')}: <strong className="text-gray-600 dark:text-gray-400">{stats.closed}</strong></span>
        </div>
      </div>

      {/* Painel direito - Conversa ou placeholder */}
      <div className="flex-1 hidden md:flex flex-col">
        {selectedId ? (
          <ConversationView id={selectedId} embedded />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
              <Send size={28} className="text-green-400 dark:text-green-500" />
            </div>
            <p className="text-gray-600 dark:text-slate-300 font-medium">{t('conversations.selectConversation')}</p>
            <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">{t('conversations.chooseToStart')}</p>

            <div className="mt-6 text-left bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4 max-w-sm">
              <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">💡 {t('conversations.tip')}:</p>
              <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {t('conversations.tip1')}</li>
                <li>• {t('conversations.tip2')}</li>
                <li>• {t('conversations.tip3')}</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationsList;
